import {useCallback, useMemo, useState} from 'react';

import {useAppSelector} from 'app/store/hooks';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {selectToken} from '@/app/store/tokenSlice';
import {useCountryCode, useSkipPhoneValidation} from '@/hooks';
import {AmplifyService} from '@/services';
import logError from '@/utils/errors/logError';

export const useActions = () => {
  //#region [---- [PROPERTIES] ----]
  const navigate = useNavigate();
  const {handleSignOut} = AmplifyService();
  const {t} = useTranslation();
  const token = useAppSelector(selectToken);
  const {
    isCountryCodeVisible,
    setCountryCodeVisibleStorage,
    removeCountryCodeVisibleStorage,
  } = useCountryCode();
  const {
    isSkipPhoneValidationEnabled,
    setSkipPhoneValidationStorage,
    removeSkipPhoneValidationStorage,
  } = useSkipPhoneValidation();
  //#endregion

  //#region [---- [HOOKS] ----]
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  //#endregion

  //#region [---- [LOGIC] ----]
  const handleDevToolsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleDevToolsClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCopyToken = useCallback(async () => {
    handleDevToolsClose();
    if (!token) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(token);
      } else {
        console.warn('Clipboard API is not available');
      }
    } catch (error) {
      logError(error, {
        fileName: 'DevOptionsMenu useActions',
        functionName: 'handleCopyToken',
      });
    }
  }, [handleDevToolsClose, token]);

  const handleClearSession = useCallback(async () => {
    try {
      handleDevToolsClose();
      await handleSignOut();
      navigate('/authentication/sign-up');
      window.location.reload();
    } catch (error) {
      logError(error, {
        fileName: 'DevOptionsMenu useActions',
        functionName: 'handleClearSession',
      });
    }
  }, [handleDevToolsClose, handleSignOut, navigate]);

  const handleGoToSignIn = useCallback(async () => {
    try {
      handleDevToolsClose();
      await handleSignOut();
      navigate(
        '/authentication/sign-in/zAf-MYG-Cqp-NSR-c6V-zfb-T7A-pKY-rr2-mwW',
      );
    } catch (error) {
      logError(error, {
        fileName: 'DevOptionsMenu useActions',
        functionName: 'handleGoToSignIn',
      });
    }
  }, [handleDevToolsClose, handleSignOut, navigate]);

  const handleIsCountryCodeVisible = useCallback(async () => {
    handleDevToolsClose();
    if (isCountryCodeVisible) {
      removeCountryCodeVisibleStorage();
    } else {
      setCountryCodeVisibleStorage(!isCountryCodeVisible);
    }

    showSnackbar({
      summary: t('dev_buttons.refresh_page_title'),
      message: t('dev_buttons.refresh_page_description'),
      variant: SnackbarType.INFO,
    });
  }, [
    handleDevToolsClose,
    isCountryCodeVisible,
    removeCountryCodeVisibleStorage,
    setCountryCodeVisibleStorage,
    t,
  ]);

  const handleIsSkipPhoneValidationEnabled = useCallback(async () => {
    handleDevToolsClose();
    if (isSkipPhoneValidationEnabled) {
      removeSkipPhoneValidationStorage();
    } else {
      setSkipPhoneValidationStorage(!isSkipPhoneValidationEnabled);
    }

    showSnackbar({
      summary: t('dev_buttons.refresh_page_title'),
      message: t('dev_buttons.refresh_page_description'),
      variant: SnackbarType.INFO,
    });
  }, [
    handleDevToolsClose,
    isSkipPhoneValidationEnabled,
    removeSkipPhoneValidationStorage,
    setSkipPhoneValidationStorage,
    t,
  ]);

  const handleNavigateToTeenSignIn = useCallback(async () => {
    handleDevToolsClose();
    try {
      await handleSignOut();
      navigate('/authentication/sign-up?role=teen');
      window.location.reload();
    } catch (error) {
      logError(error, {
        fileName: 'DevOptionsMenu useActions',
        functionName: 'handleNavigateToTeenSignIn',
      });
    }
  }, [handleDevToolsClose, handleSignOut, navigate]);

  const handleNavigateToMoAIAssistant = useCallback(async () => {
    handleDevToolsClose();
    try {
      navigate('/split-bills');
    } catch (error) {
      logError(error, {
        fileName: 'DevOptionsMenu useActions',
        functionName: 'handleNavigateToMoAIAssistant',
      });
    }
  }, [handleDevToolsClose, navigate]);
  //#endregion

  //#region [---- [MEMOS] ----]
  const devButtonCountryCodeLabel: string = useMemo(() => {
    return isCountryCodeVisible
      ? t('dev_buttons.hide_country_code')
      : t('dev_buttons.show_country_code');
  }, [isCountryCodeVisible, t]);

  const devButtonSkipPhoneValidationLabel: string = useMemo(() => {
    return isSkipPhoneValidationEnabled
      ? t('dev_buttons.disable_skip_phone_validation')
      : t('dev_buttons.enable_skip_phone_validation');
  }, [isSkipPhoneValidationEnabled, t]);
  //#endregion

  return {
    token,
    anchorEl,
    open,
    devButtonCountryCodeLabel,
    devButtonSkipPhoneValidationLabel,
    handleDevToolsClick,
    handleDevToolsClose,
    handleCopyToken,
    handleClearSession,
    handleGoToSignIn,
    handleIsCountryCodeVisible,
    handleIsSkipPhoneValidationEnabled,
    handleNavigateToTeenSignIn,
    handleNavigateToMoAIAssistant,
  };
};
